import { useEffect, useRef, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import {
  GoAButton,
  GoAButtonGroup,
  GoACallout,
  GoAFileUploadCard,
  GoAFileUploadInput,
  GoAFormItem,
  GoAModal,
  GoASpacer,
} from '@abgov/react-components';
import {
  DamageSketch,
  DesktopDamageSelectorLayoutConfiguration,
  MobileDamageSelectorLayoutConfiguration,
} from '../sketches/damage';
import { Collision, DamageSelection, FormStep } from '../../models/collision';
import { InitialPointOfImpact } from '../../models/initial-point-of-impact';
import p5 from 'p5';
import { useIsDesktop } from '../../utils/isDesktop';

import {
  uploadMedia,
  deleteMedia,
  dismissError,
  fetchMediaListByCollisionId,
  addToQueue,
} from '../../state/slices/mediaSlice';
import useCsrService from '../../services/csr-api-redux';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import { updateCollision } from '../../state/slices/collisionSlice';
import {
  isHitAndRun,
  setReviewMediaUpdated,
} from '../../state/slices/configurationSlice';
import { ImageCard } from './image-card-component';
import {
  getReturnToValue,
  IsInReviewEditMode as isInReviewEditMode,
} from '../wizard/wizard-component';

export const queueMap = new Map();

interface MediaManagerProps extends BaseWizardProps {
  maxNumber: number;
}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const MediaManagerComponent = ({
  navigationConfiguration,
  maxNumber,
}: MediaManagerProps) => {
  const csrService = useCsrService();
  const dispatch = useAppDispatch();
  const isDesktop = useIsDesktop();

  const p5Ref = useRef<HTMLDivElement>(null);
  const containerDiv = useRef<HTMLDivElement>(null);

  const uploadErrorText =
    'File could not be uploaded, possibly due to connection error. Try again.';

  const [uploadInputError, setUploadInputError] = useState('');

  const [uploadError, setUploadError] = useState('');

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );
  const stateMedia = useAppSelector((state: RootState) => state.media);
  const [reviewMediaUpdate, setReviewMediaUpdate] = useState(false);
  const [currentUpload, setCurrentUpload] = useState<string | null>(null);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUploaderForError, setShowUploaderForError] = useState(false);
  const [fileToDelete, setFileToDelete] = useState('');
  const [showReturnToReviewModal, setShowReturnToReviewModal] = useState(false);
  const [
    releventPointOfImpactLocationList,
    setReleventPointOfImpactLocationList,
  ] = useState<InitialPointOfImpact[]>([]);

  const [
    initialPointOfImpactLocationList,
    setInitialPointOfImpactLocationList,
  ] = useState<InitialPointOfImpact[]>([]);

  const [damageSelectionsList, setDamageSelectionsList] = useState<
    DamageSelection[]
  >(stateCollision.collision.vehicles?.[0]?.damage?.damageSelection ?? []);

  const [damageSketch, setDamageSketch] = useState<DamageSketch>(
    new DamageSketch(),
  );

  const [displayNoImagesUploadedCallout, setDisplayNoImagesUploadedCallout] =
    useState(false);

  useEffect(() => {
    resetMediaList();
    if (stateConfiguration.uiTriggers.collisionTypeUpdated) {
      dispatch(
        pushNotification({
          body: "You've changed your collision type. Review or update your photos to ensure they align with the new information",
          notificationType: NotificationType.importantWarning,
        }),
      );
    }

    if (
      stateConfiguration.uiTriggers.reviewDamageModified ||
      stateConfiguration.uiTriggers.reviewPointOfImpactModified
    ) {
      dispatch(
        pushNotification({
          body: "You've updated your vehicle damage information. Update your vehicle damage photos where applicable",
          notificationType: NotificationType.importantWarning,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (stateMedia.images && stateMedia.images.length > 0) {
      setFileNames([]);
      const fileNames: string[] = [];

      stateMedia.images.forEach((image: any) => {
        fileNames.push(image);
      });

      setFileNames(fileNames);
    }
  }, [stateMedia.images]);

  useEffect(() => {
    if (stateMedia.error) {
      setShowUploaderForError(true);

      if (
        stateMedia.error.toLowerCase() == 'an error occurred' ||
        stateMedia.error.toLowerCase() == 'network error'
      ) {
        setUploadError(uploadErrorText);
      } else {
        setUploadInputError(stateMedia.error);
      }
    }
  }, [stateMedia.error]);

  useEffect(() => {
    let pageConfig = 'error';

    if (
      isHitAndRun(stateCollision.collision.collisionType!, stateConfiguration)
    ) {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.hitandrun !== undefined,
      )!.hitandrun;
    } else {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.done !== undefined,
      )!.done;
    }

    setNextPage(pageConfig);
  }, []);

  const [nextPage, setNextPage] = useState('');

  useEffect(() => {
    const processQueue = async () => {
      if (!stateMedia.isUploading && stateMedia.queue.length > 0) {
        const fileToUpload = queueMap.get(stateMedia.queue[0].fileId);

        const props = {
          collisionId: stateCollision.collision.id!.toString(),
          vehicleId: '1', // hard coded for now
          file: fileToUpload,
          csrService,
          dispatch,
        };

        dispatch(uploadMedia(props));
      }
    };

    processQueue();
  }, [stateMedia.queue, stateMedia.isUploading, dispatch]);

  useEffect(() => {
    if (!stateMedia.isUploading) {
      resetMediaList();
    }
  }, [stateMedia.isUploading]);

  useEffect(() => {
    const pois: InitialPointOfImpact[] = [];

    stateConfiguration.configuration.pointOfImpact.forEach((poi: any) => {
      pois.push(new InitialPointOfImpact(poi.id, poi.value));
    });

    setInitialPointOfImpactLocationList(pois);
  }, [stateConfiguration.configuration.pointOfImpact]);

  useEffect(() => {
    const damageSelectionsListValues = damageSelectionsList.map((item) =>
      item.pointOfImpactId?.toString(),
    );

    const releventDamageLocations = initialPointOfImpactLocationList.filter(
      (item) => damageSelectionsListValues.includes(item.id.toString()),
    );

    setReleventPointOfImpactLocationList(releventDamageLocations);
  }, [initialPointOfImpactLocationList, damageSelectionsList]);

  useEffect(() => {
    if (p5Ref.current) {
      let startCount = 1;

      const p5Instance = new p5(
        damageSketch.sketch(
          containerDiv.current!,
          damageSelectionsList,
          releventPointOfImpactLocationList,
          startCount,
          () => {},
          false,
          isDesktop
            ? new DesktopDamageSelectorLayoutConfiguration(
                releventPointOfImpactLocationList,
              )
            : new MobileDamageSelectorLayoutConfiguration(
                releventPointOfImpactLocationList,
              ),

          true,
        ),
        p5Ref.current,
      );

      return () => {
        p5Instance.remove();
      };
    }
  }, [releventPointOfImpactLocationList]);

  const resetMediaList = () => {
    dispatch(
      fetchMediaListByCollisionId({
        id: stateCollision.collision.id!,
        csrService,
      }),
    );
  };

  const uploadFile = (file: File) => {
    setDisplayNoImagesUploadedCallout(false);
    setUploadError('');
    setUploadInputError('');
    dispatch(removeAllValidationErrorNotifications());

    if (isInReviewEditMode()) {
      setReviewMediaUpdate(true);
      dispatch(setReviewMediaUpdated(true));
    }

    if (fileNames.length + 1 <= maxNumber) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (!e.target) return;

        setCurrentUpload(file.name);

        const id = crypto.randomUUID();

        const uploadProps = {
          collisionId: stateCollision.collision.id!.toString(),
          vehicleId: '1', // hard coded for now
          fileId: id,
        };

        queueMap.set(id, file);

        dispatch(addToQueue(uploadProps));
      };

      reader.readAsDataURL(file);
    } else {
      dispatch(
        pushNotification({
          body: 'Maximum number of images uploaded',
          notificationType: NotificationType.importantValidationError,
        }),
      );
    }
  };

  const loadImage = async (filename: string): Promise<string> => {
    try {
      const result = await csrService.get(
        'api/v1/media/images/collisions/' + filename,
        {
          responseType: 'arraybuffer',
          headers: {
            'Content-Type': 'image/png',
          },
        },
      );
      return Promise.resolve<string>(result.data);
    } catch (error: any) {
      console.error('Error getting image', error);
      return Promise.reject('');
    }
  };

  const handleFileDelete = (imageName: string) => {
    setFileToDelete(imageName);
    setShowDeleteModal(true);
  };

  const deleteFile = (imageName: string) => {
    console.log('Deleting file: ', imageName);
    setFileNames((uploads) => uploads.filter((x) => x !== imageName));

    dispatch(deleteMedia({ imageName, csrService })).finally(() => {
      resetMediaList();
    });

    if (isInReviewEditMode()) {
      dispatch(setReviewMediaUpdated(true));
      setReviewMediaUpdate(true);
    }

    setShowDeleteModal(false);
  };

  const isAtMaxNumberFiles = () => {
    if (fileNames.length >= maxNumber) return true;

    return false;
  };

  const validate = (): boolean => {
    let valid = true;
    setDisplayNoImagesUploadedCallout(false);

    if (fileNames.length == 0) {
      valid = false;
      setDisplayNoImagesUploadedCallout(true);
    }

    return valid;
  };

  const handleNavigate = () => {
    if (isInReviewEditMode()) {
      setShowReturnToReviewModal(true);
    } else {
      navigationConfiguration.navigate(nextPage);
    }
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const images = stateMedia.images.length > 0;

      let completed = false;
      let inProgress = true;

      if (!images) {
        completed = false;
        inProgress = true;
      } else {
        completed = true;
        inProgress = false;
      }

      if (
        stateConfiguration.uiTriggers.reviewDamageModified &&
        !reviewMediaUpdate
      ) {
        completed = false;
        inProgress = true;
      }
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        completed,
        inProgress,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }
    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <GoAModal
        open={showReturnToReviewModal}
        type=""
        calloutVariant="important"
        actions={
          <GoAButtonGroup alignment="end">
            <GoAButton
              type="tertiary"
              onClick={() => {
                setShowReturnToReviewModal(false);
              }}
            >
              Cancel
            </GoAButton>
            <GoAButton
              type="primary"
              onClick={() => {
                setShowReturnToReviewModal(false);
                save();
                navigationConfiguration.navigate('#review');
              }}
            >
              View report summary
            </GoAButton>
          </GoAButtonGroup>
        }
        heading={!reviewMediaUpdate ? '' : 'Your photos have been updated'}
      >
        <div
          style={{
            display: reviewMediaUpdate ? 'block' : 'none',
            font: 'var(--goa-typography-body-l)',
          }}
        >
          Your photos have been updated
        </div>{' '}
        Click 'View report summary' to return to the report review page.
      </GoAModal>
      <GoAModal
        open={showDeleteModal}
        maxWidth={isDesktop ? '350px' : '300'}
        heading="Are you sure you want to remove this photo?"
        actions={
          <div>
            <GoAButtonGroup alignment="end">
              <div style={{ maxWidth: '250px' }}>
                <GoAButton
                  type="tertiary"
                  onClick={() => {
                    setShowDeleteModal(false);
                  }}
                >
                  Cancel
                </GoAButton>
              </div>
              <div style={{ maxWidth: '250px' }}>
                <GoAButton
                  type="primary"
                  onClick={() => {
                    deleteFile(fileToDelete);
                  }}
                >
                  Remove file
                </GoAButton>
              </div>
            </GoAButtonGroup>
          </div>
        }
        role="dialog"
      ></GoAModal>
      <h2>Upload vehicle damage photos</h2>
      <GoASpacer vSpacing={'l'} />
      Refer to the vehicle diagram below to help you upload photos for the
      area(s) of damage you've indicated.
      <GoASpacer vSpacing={'l'} />
      <div ref={containerDiv} className="damage-vehicle">
        <div ref={p5Ref} />
      </div>
      <GoASpacer vSpacing={'l'} />
      Upload a maximum of {maxNumber} photos. They can be uploaded one at a time
      or as a batch (if applicable).
      <GoASpacer vSpacing={'l'} />
      <GoAFormItem label={'Upload photos'}>
        <div style={{ display: isAtMaxNumberFiles() ? 'none' : 'block' }}>
          <GoAFormItem error={uploadInputError}>
            <GoAFileUploadInput
              onSelectFile={uploadFile}
              maxFileSize="10MB"
              accept="image/jpg,image/jpeg,image/png,image/heic,image/heif,image/bmp,image/webp,jpg,jpeg,png,heic,heif,bmp,webp"
            ></GoAFileUploadInput>
          </GoAFormItem>
        </div>
        <GoASpacer vSpacing={'xl'} />
        <div
          style={{ display: displayNoImagesUploadedCallout ? 'block' : 'none' }}
        >
          <GoACallout type="important" size="medium">
            Upload an image to to continue or click View report summary if you
            want to upload images later
          </GoACallout>
        </div>
        <div style={{ display: isAtMaxNumberFiles() ? 'block' : 'none' }}>
          <GoACallout type="important">
            {`You've reached the maximum number of ${maxNumber} uploads`}
          </GoACallout>
        </div>

        {(showUploaderForError || stateMedia.isUploading) && (
          <GoAFileUploadCard
            key={stateMedia.currentMediaName}
            filename={stateMedia.currentMediaName}
            size={stateMedia.currentImageSize}
            progress={stateMedia.currentImageProgress}
            error={uploadError}
            onCancel={() => {
              dispatch(removeAllValidationErrorNotifications());
              dispatch(dismissError());
              setCurrentUpload(null);
              setShowUploaderForError(false);
              setUploadError('');
              setUploadInputError('');
            }}
            onDelete={() => {
              dispatch(removeAllValidationErrorNotifications());
              dispatch(dismissError());
              setCurrentUpload(null);
              setShowUploaderForError(false);
              setUploadError('');
              setUploadInputError('');
            }}
          />
        )}
        <div
          style={
            isDesktop
              ? {}
              : {
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }
          }
        >
          <div className="grid-container">
            {fileNames.map((fileName, index) => {
              <GoASpacer vSpacing={'xl'} />;
              if (fileName)
                return (
                  <div key={fileName}>
                    <ImageCard
                      fileName={fileName}
                      axiosInstance={csrService}
                      deleteFile={handleFileDelete}
                      imagePromise={loadImage}
                      showDeleteFile={true}
                      newKey={fileName}
                    />
                  </div>
                );
            })}
          </div>
        </div>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        buttonText="Save and continue"
        goToReview={navigationConfiguration.goToReview}
        showViewSummaryButton={!isInReviewEditMode()}
        save={save}
        validate={validate}
        navigate={handleNavigate}
      />
    </div>
  );
};
